import { useCallback, useEffect, useMemo, useState } from "react";
import type { PbjModel, PbjModelUpsertBody } from "../../../utils/models";
import { defaultPbjModel } from "../PredictionTab/nationalDefaults";
import { useUpsertPbjModel } from "./useUpsertPbjModel";

type UsePbjModelDraftParams = {
	// Active department id — every calculate persists against this dept.
	deptId: string;
	// The dept's persisted config (from userInfo → chosenDepartment.pbjModel). Undefined for a
	// first-time dept, in which case we seed from the CMS-snapshot defaults.
	savedModel: PbjModel | undefined;
	// Called after a successful persist so the page can advance its committed date window (the
	// prediction/audit query keys). Kept a callback so the date window stays page-session state.
	onCommitted: () => void;
};

type UsePbjModelDraft = {
	savedRow: PbjModel;
	configDraft: PbjModelUpsertBody;
	setConfigDraft: React.Dispatch<React.SetStateAction<PbjModelUpsertBody>>;
	isDirty: boolean;
	runCalculate: (sourceDraft: PbjModelUpsertBody) => Promise<void>;
	isCalculating: boolean;
};

/**
 * Owns the PBJ Model editing buffer: the saved server row, the live `configDraft`, the
 * dirty check, and the persist-then-recompute action. Extracted from PbjModelPage so the
 * dirty check can't drift from the schema (see `isDirty` below).
 */
export const usePbjModelDraft = ({ deptId, savedModel, onCommitted }: UsePbjModelDraftParams): UsePbjModelDraft => {
	// Server is the source of truth for per-dept config. When a dept has never saved a config
	// we seed from the CMS-snapshot defaults so the form is editable immediately.
	// DEFAULT: first-time-dept fallback only — an unsaved dept has no row, so we mirror the BE
	// PbjModel.default() factory rather than block the page. Any real saved row wins.
	const savedRow = useMemo<PbjModel>(() => savedModel ?? defaultPbjModel(deptId), [savedModel, deptId]);

	const [configDraft, setConfigDraft] = useState<PbjModelUpsertBody>(savedRow);

	// Re-seed the live draft from the active dept's saved row whenever it changes (dept switch
	// or a successful save that refreshed userInfo), replacing the editing buffer with the
	// persisted values.
	useEffect(() => {
		setConfigDraft(savedRow);
	}, [savedRow]);

	const upsert = useUpsertPbjModel();

	// Keyed diff over EVERY field of the saved row — a hand-listed comparison silently drops
	// new columns (national-benchmark HRDs, longShiftThreshold) and leaves Calculate disabled
	// after editing them. `savedRow` and `configDraft` share the full PbjModelUpsertBody key set
	// and every field is a scalar (string | number | boolean | null), so a shallow `!==` per key
	// is a correct dirty check that can never fall behind the schema.
	const isDirty = useMemo(() => (Object.keys(savedRow) as (keyof PbjModelUpsertBody)[]).some((k) => configDraft[k] !== savedRow[k]), [savedRow, configDraft]);

	// Persist + commit a specific draft, then recompute. Taking the draft as an argument (rather
	// than reading `configDraft`) lets callers that just mutated the draft — e.g. the manual-turnover
	// dialog's Save — recalculate with the new values immediately, without waiting for a state flush.
	const runCalculate = useCallback(
		async (sourceDraft: PbjModelUpsertBody) => {
			const body: PbjModelUpsertBody = { ...sourceDraft, departmentId: deptId };
			await upsert.mutateAsync(body);
			setConfigDraft(body);
			onCommitted();
		},
		[deptId, upsert, onCommitted],
	);

	return { savedRow, configDraft, setConfigDraft, isDirty, runCalculate, isCalculating: upsert.isPending };
};
